import { useEffect, useState } from "react";
import { CheckCircle2, CircleDashed, Copy, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Card, LoadingBlock, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const RunItem = ({ r }) => {
  const [open, setOpen] = useState(false);
  const files = r.files || [];
  return (
    <li className="rounded-xl bg-slate-50 p-3 text-sm">
      <div className="flex justify-between gap-2"><span className={r.status === "ok" ? "font-medium" : "font-medium text-red-600"}>{r.message}</span><span className="whitespace-nowrap text-slate-400">{fmtDateTime(r.at)} · {r.trigger === "auto" ? "Automatic" : r.by}</span></div>
      {files.length > 0 && <button className="mt-1 text-xs font-medium text-indigo-600" onClick={() => setOpen(!open)} data-testid={`gmail-run-toggle-${r.id}`}>{open ? "Hide files" : `Show ${files.length} files`}</button>}
      {open && files.map((f, i) => (
        <div key={i} className="mt-1 text-xs text-slate-500">{f.file} — {f.status === "ok" ? `${f.new} new, ${f.updated} updated, ${f.skipped} already saved${f.errors ? `, ${f.errors} problem rows` : ""}` : f.message}</div>
      ))}
    </li>
  );
};

const Runs = ({ version }) => {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    api.get("/gmail/runs").then((r) => setRuns(r.data)).catch(() => setRuns([]));
  }, [version]);
  if (!runs) return <LoadingBlock rows={2} />;
  if (!runs.length) return <p className="text-sm text-slate-400">No fetches yet.</p>;
  return (
    <ul className="space-y-3" data-testid="gmail-runs">
      {runs.map((r) => <RunItem key={r.id} r={r} />)}
    </ul>
  );
};

export default function GmailTab() {
  const [g, setG] = useState(null);
  const [f, setF] = useState({ client_id: "", client_secret: "", sender: "", auto_enabled: true });
  const [busy, setBusy] = useState("");
  const [version, setVersion] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const load = () => api.get("/gmail").then((r) => { setG(r.data); setF((x) => ({ ...x, client_id: x.client_id || r.data.client_id, sender: x.sender || r.data.sender, auto_enabled: r.data.auto_enabled })); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (!g?.running) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.get("/gmail").catch(() => ({ data: null }));
      if (data && !data.running) {
        setG(data);
        setVersion((v) => v + 1);
        data.last_status === "error" ? toast.error(data.last_message) : toast.success(data.last_message);
      }
    }, 4000);
    return () => clearInterval(t);
  }, [g?.running]);
  useEffect(() => {
    if (!waiting) return undefined;
    const started = Date.now();
    const t = setInterval(async () => {
      const { data } = await api.get("/gmail").catch(() => ({ data: null }));
      if (data?.connected && data.connected_at !== g?.connected_at) {
        setG(data);
        setWaiting(false);
        toast.success(`Gmail connected: ${data.email}`, { id: "gmail-oauth" });
      } else if (Date.now() - started > 300000) {
        setWaiting(false);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [waiting, g?.connected_at]);
  const act = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      load();
      setVersion((v) => v + 1);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  if (!g) return <LoadingBlock />;
  const connect = async () => {
    // open the tab during the click so popup blockers allow it; Google refuses to load inside the preview frame
    const win = window.open("about:blank", "_blank");
    setBusy("connect");
    try {
      const { data } = await api.get("/oauth/gmail/start");
      if (win) win.location.href = data.url;
      else window.top.location.href = data.url;
      setWaiting(true);
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-6" data-testid="gmail-tab">
      <Card testid="gmail-status-card">
        <div className="flex flex-wrap items-center gap-4">
          {g.connected ? <CheckCircle2 className="h-10 w-10 text-emerald-500" /> : <CircleDashed className="h-10 w-10 text-slate-300" />}
          <div className="flex-1">
            <div className="text-lg font-semibold" data-testid="gmail-connection-status">{g.connected ? `Reading ${g.email}` : "Gmail not connected"}</div>
            <div className="text-sm text-slate-500">Restaverse files from <b>{g.sender}</b> are imported every day at 11:00 AM (IST). Reelo files still need to be uploaded by hand.</div>
            {g.last_run_at && g.connected && <div className={`mt-1 text-sm ${g.last_status === "error" ? "text-red-600" : "text-slate-500"}`} data-testid="gmail-last-run">Last check: {fmtDateTime(g.last_run_at)} · {g.last_message}</div>}
          </div>
          {g.connected && (
            <div className="flex gap-2">
              <Button className="rounded-xl" onClick={() => act("fetch", async () => { const r = (await api.post("/gmail/fetch")).data; toast.info(r.message); })} disabled={!!busy || g.running} data-testid="gmail-fetch-button">{busy === "fetch" || g.running ? <Spinner /> : <RefreshCw className="mr-2 h-4 w-4" />}{g.running ? "Checking inbox…" : "Check inbox now"}</Button>
              <Button variant="outline" className="rounded-xl" onClick={() => window.confirm("Stop reading this mailbox? Automatic imports will stop until you reconnect.") && act("dis", () => api.post("/gmail/disconnect"))} data-testid="gmail-disconnect-button">Disconnect</Button>
            </div>
          )}
        </div>
      </Card>
      <Card title="Connection settings" subtitle="Create a Google 'OAuth client' (Web application) once — see the Help tab." testid="gmail-settings-card">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2"><Label>Client ID</Label><Input data-testid="gmail-client-id-input" className="h-11 rounded-xl" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} placeholder="xxxx.apps.googleusercontent.com" /></div>
          <div className="space-y-2"><Label>Client secret</Label><Input data-testid="gmail-client-secret-input" type="password" className="h-11 rounded-xl" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} placeholder={g.configured ? "Saved — paste only to replace" : ""} /></div>
          <div className="space-y-2"><Label>Restaverse sender address</Label><Input data-testid="gmail-sender-input" className="h-11 rounded-xl" value={f.sender} onChange={(e) => setF({ ...f, sender: e.target.value })} /></div>
          <label className="flex items-center gap-3 self-end pb-2 text-sm font-medium"><Switch checked={f.auto_enabled} onCheckedChange={(v) => setF({ ...f, auto_enabled: v })} data-testid="gmail-auto-switch" />Check automatically every day at 11 AM</label>
          <div className="space-y-2 md:col-span-2">
            <Label>Authorized redirect URI (paste this in Google Cloud)</Label>
            <div className="flex gap-2"><Input readOnly value={g.redirect_uri} className="h-11 rounded-xl font-mono text-xs" data-testid="gmail-redirect-uri" /><Button variant="outline" className="h-11 rounded-xl" data-testid="gmail-copy-redirect" onClick={() => { navigator.clipboard.writeText(g.redirect_uri); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button></div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button className="h-11 rounded-xl" disabled={!!busy} onClick={() => act("save", async () => { await api.put("/gmail", f); toast.success("Saved"); setF((x) => ({ ...x, client_secret: "" })); })} data-testid="gmail-save-button">{busy === "save" ? <Spinner /> : "Save"}</Button>
          <Button variant="outline" className="h-11 rounded-xl" disabled={!g.configured || !!busy} onClick={connect} data-testid="gmail-connect-button"><Mail className="mr-2 h-4 w-4" />{g.connected ? "Reconnect Gmail" : "Sign in with Google"}</Button>
        </div>
        {waiting && (
          <div data-testid="gmail-waiting" className="mt-4 flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
            <Spinner />Google sign-in opened in a new tab. Finish there, and this page updates by itself.
            <button className="ml-auto font-medium underline" onClick={() => setWaiting(false)} data-testid="gmail-waiting-cancel">Cancel</button>
          </div>
        )}
      </Card>
      <Card title="Recent inbox checks"><Runs version={version} /></Card>
    </div>
  );
}
